import { useId, type ComponentProps } from "react";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@volca/ui/components/ui/field";
import { Input } from "@volca/ui/components/ui/input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import {
  AP_ACTION_TYPE,
  type ApAgentAction,
  type ApPoLineItem,
  type ApQboBillPayload,
} from "@/accounts-payable/types";
import { formatDollars, humanizePoField } from "@/accounts-payable/utils";
import { LineItemsEditor } from "./line-items-editor";
import { QboBillEditor } from "./qbo-bill-editor";
import { CostChangePct, SectionLabel } from "./shared";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = Record<string, any>;

const PayloadField = ({
  label,
  description,
  ...props
}: { label: string; description?: string } & ComponentProps<typeof Input>) => {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} {...props} />
      {description && <FieldDescription>{description}</FieldDescription>}
    </Field>
  );
};

/** The free-text reason, full width like every other field. */
const ReasonField = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) => (
  <Stack className="w-full" align="start" gap={1}>
    <SectionLabel>Reason</SectionLabel>
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Reason"
      rows={3}
    />
  </Stack>
);

/** Editable form for a pending action's proposed payload. */
export const EditablePayload = ({
  action,
  businessId,
  draft,
  setDraft,
}: {
  action: ApAgentAction;
  /** For the pickers that search the business's records (QuickBooks bill). */
  businessId: string | null;
  draft: Draft;
  setDraft: (next: Draft) => void;
}) => {
  const set = (key: string, value: unknown) =>
    setDraft({ ...draft, [key]: value });
  const setItem = (index: number, key: string, value: unknown) => {
    const items = (draft.items as ApPoLineItem[]).map((it, i) =>
      i === index ? { ...it, [key]: value } : it,
    );
    setDraft({ ...draft, items });
  };
  const setChange = (key: string, value: unknown) =>
    set("changes", { ...draft.changes, [key]: value });
  const setChangeItem = (index: number, key: string, value: unknown) => {
    const items = (draft.changes.items as ApPoLineItem[]).map((it, i) =>
      i === index ? { ...it, [key]: value } : it,
    );
    setChange("items", items);
  };

  switch (action.action_type) {
    case AP_ACTION_TYPE.matchJob:
      // A match names a job or a project; the field edits whichever the
      // proposal named, so clearing it to retype can't switch to the other.
      return action.payload.st_project_external_id ? (
        <PayloadField
          label="Project #"
          type="text"
          inputMode="numeric"
          value={draft.st_project_external_id}
          onChange={(e) => set("st_project_external_id", e.target.value)}
        />
      ) : (
        <PayloadField
          label="Job #"
          type="text"
          inputMode="numeric"
          value={draft.st_job_external_id}
          onChange={(e) => set("st_job_external_id", e.target.value)}
        />
      );
    case AP_ACTION_TYPE.linkVendor:
      return (
        <Stack className="w-full" align="start" gap={4}>
          <PayloadField
            label="Vendor #"
            type="text"
            inputMode="numeric"
            value={draft.st_vendor_id}
            onChange={(e) => set("st_vendor_id", e.target.value)}
          />
          <PayloadField
            label="Vendor name"
            value={draft.st_vendor_name}
            onChange={(e) => set("st_vendor_name", e.target.value)}
          />
        </Stack>
      );
    case AP_ACTION_TYPE.updatePricebookCost: {
      const current = Number(draft.current_cost) || 0;
      const proposed = Number(draft.proposed_cost) || 0;
      // A chargeable-only change leaves the cost out, so there is no cost to edit.
      const costLeftOut =
        draft.proposed_cost == null && draft.chargeable != null;
      return (
        <Stack className="w-full" align="start" gap={4}>
          <Stack
            align="start"
            direction="row"
            gap={8}
            className="w-full flex-wrap"
          >
            <Stack align="start" gap={1}>
              <SectionLabel>SKU</SectionLabel>
              <Text className="font-mono">{draft.sku}</Text>
            </Stack>
            {!costLeftOut && (
              <Stack align="start" gap={1}>
                <SectionLabel>Current cost</SectionLabel>
                <Text className="tabular-nums">
                  {formatDollars(draft.current_cost)}
                </Text>
              </Stack>
            )}
          </Stack>
          {!costLeftOut && (
            <>
              <PayloadField
                label="Recommended cost ($)"
                type="text"
                inputMode="decimal"
                value={draft.proposed_cost}
                onChange={(e) => set("proposed_cost", e.target.value)}
              />
              <CostChangePct
                current={current}
                proposed={proposed}
                suffix="vs current"
                className="text-sm"
              />
            </>
          )}
        </Stack>
      );
    }
    case AP_ACTION_TYPE.createPricebookItem:
      // Same order and labels as the view. Type is shown, not edited: the
      // item's type is decided by the pricebook section it's created in.
      return (
        <Stack className="w-full" align="start" gap={4}>
          <PayloadField
            label="Name"
            value={draft.name}
            onChange={(e) => set("name", e.target.value)}
          />
          <PayloadField
            label="Description"
            value={draft.description}
            onChange={(e) => set("description", e.target.value)}
          />
          <PayloadField
            label="Code"
            value={draft.code}
            onChange={(e) => set("code", e.target.value)}
          />
          <Stack className="w-full" direction="row" gap={4} align="end">
            {draft.item_type && (
              <Stack align="start" gap={1} className="min-w-0 flex-1">
                <SectionLabel>Type</SectionLabel>
                <Text>
                  {draft.item_type.charAt(0).toUpperCase() +
                    draft.item_type.slice(1)}
                </Text>
              </Stack>
            )}
            <PayloadField
              label="Cost ($)"
              type="text"
              inputMode="decimal"
              value={draft.cost}
              onChange={(e) => set("cost", e.target.value)}
            />
          </Stack>
        </Stack>
      );
    case AP_ACTION_TYPE.updatePo: {
      // Same order as the view: the scalar fields, then the line items. The
      // `items` change is a line-item array, edited through its sub-fields
      // rather than bound to a single text input.
      const changes = Object.entries(
        draft.changes as Record<string, string | ApPoLineItem[]>,
      );
      const items = changes.find(([, v]) => Array.isArray(v));
      return (
        <Stack className="w-full" align="start" gap={4}>
          {changes
            .filter(([, v]) => !Array.isArray(v))
            .map(([k, v]) => (
              <PayloadField
                key={k}
                label={humanizePoField(k)}
                description={
                  k === "memo"
                    ? "Only used if the PO's summary is blank."
                    : undefined
                }
                value={v as string}
                onChange={(e) => setChange(k, e.target.value)}
              />
            ))}
          {items && (
            <LineItemsEditor
              items={items[1] as ApPoLineItem[]}
              onChange={setChangeItem}
            />
          )}
          <ReasonField
            value={draft.reason ?? ""}
            onChange={(next) => set("reason", next)}
          />
        </Stack>
      );
    }
    case AP_ACTION_TYPE.createReceipt:
      return (
        <LineItemsEditor
          items={draft.items as ApPoLineItem[]}
          onChange={setItem}
        />
      );
    case AP_ACTION_TYPE.createPo:
    case AP_ACTION_TYPE.createReturn:
    case AP_ACTION_TYPE.replaceReceipt:
      return (
        <Stack className="w-full" align="start" gap={4}>
          <ReasonField
            value={draft.reason ?? ""}
            onChange={(next) => set("reason", next)}
          />
          <LineItemsEditor
            items={draft.items as ApPoLineItem[]}
            onChange={setItem}
          />
        </Stack>
      );
    case AP_ACTION_TYPE.markNonJob:
    case AP_ACTION_TYPE.cancelReceipt:
    case AP_ACTION_TYPE.flagForReview:
    case AP_ACTION_TYPE.noAction:
      return (
        <ReasonField
          value={draft.reason ?? ""}
          onChange={(next) => set("reason", next)}
        />
      );
    case AP_ACTION_TYPE.createQboBill:
    case AP_ACTION_TYPE.updateQboBill:
      return (
        <QboBillEditor
          businessId={businessId}
          draft={draft as ApQboBillPayload}
          setDraft={setDraft}
        />
      );
    // `merge_vendor` deliberately has no form: see `hasEditablePayload`.
    default:
      return null;
  }
};
